/* ==========================================================================
   Aurum — Stack & Save ladder

   Every piece in the cart is a rung: the first takes $[first] off the order,
   each one after takes another $[step] off, up to [cap] pieces (as of
   2026-09-05: $7 a piece, capped at three — $7 / $14 / $21). Tiered
   automatic discounts in the Shopify admin apply the actual money at
   checkout, so this file NEVER touches prices and never adds lines — it only
   repaints the rails and the echo sentence as the cart changes. If it fails,
   the worst case is a stale progress display, never a wrong charge.

   The one cart mutation it performs is a cleanup: the retired gift ladder
   auto-added complimentary pieces marked `_milestone_gift`, and now that its
   Buy-X-Get-Y discount is expired those lines would be CHARGED at full
   price. Any such line found in the cart is removed once, on load.
   ========================================================================== */

/**
 * Publishes the pinned bar's height so the sticky header can park underneath
 * it. Deliberately outside the engine below: the bar renders even when the
 * engine has nothing to do, and the header would then sit behind it.
 */
(() => {
  const bar = document.querySelector('.jw-ladder-bar');
  if (!bar) return;

  const measure = () =>
    document.documentElement.style.setProperty('--jw-ladder-bar-h', `${Math.round(bar.offsetHeight)}px`);

  measure();

  if (window.ResizeObserver) new ResizeObserver(measure).observe(bar);
  else window.addEventListener('resize', measure);
})();

(() => {
  const CONFIG = window.jwStackSave;
  if (!CONFIG || !CONFIG.enabled || !Array.isArray(CONFIG.tiers) || CONFIG.tiers.length === 0) return;

  /* The offer is a TABLE, not a formula: TIERS[n-1] is the total off the
     order once the cart holds n pieces. The ramp is flat today ($7, $14, $21)
     but has been uneven before, so everything below is derived from the
     table and never assumes a constant step; changing the offer means
     changing only the table (and the matching admin discounts). */
  const TIERS = CONFIG.tiers;
  const CAP = TIERS.length;
  const FIRST = TIERS[0];
  const MAXSAVE = TIERS[CAP - 1];

  /* What one further piece adds once the ramp has levelled off — the last
     gap in the table. Derived rather than assumed, so an uneven ramp cannot
     make this copy lie. */
  const STEP = CAP > 1 ? MAXSAVE - TIERS[CAP - 2] : MAXSAVE;

  const SYNC_DEBOUNCE = 180;
  const CLEANUP_GUARD = 8;

  /** Ladder elements currently on the page, refreshed in place after a sync. */
  const ladders = new Set();

  /* ---------------------------------------------------------------- utils */

  /** Fills [token] placeholders. Mirrors the Liquid `replace` chain exactly. */
  const fill = (template, values) =>
    String(template || '').replace(/\[(\w+)\]/g, (match, token) =>
      Object.prototype.hasOwnProperty.call(values, token) ? values[token] : match
    );

  /**
   * The same substitution as `fill`, but as DOM nodes: each replaced value
   * comes back wrapped in <strong> so the dollar amounts can be styled apart
   * from the sentence. Built with createTextNode rather than an HTML string —
   * the templates are merchant settings, and nothing merchant-authored should
   * reach innerHTML.
   */
  const fillNodes = (template, values) => {
    const frag = document.createDocumentFragment();

    String(template || '')
      .split(/(\[\w+\])/g)
      .forEach((part) => {
        if (!part) return;

        const token = /^\[(\w+)\]$/.exec(part);
        if (token && Object.prototype.hasOwnProperty.call(values, token[1])) {
          const strong = document.createElement('strong');
          strong.className = 'jw-ladder__hi';
          strong.appendChild(document.createTextNode(String(values[token[1]])));
          frag.appendChild(strong);
        } else {
          frag.appendChild(document.createTextNode(part));
        }
      });

    return frag;
  };

  const isStaleGiftLine = (line) => Boolean(line.properties && line.properties[CONFIG.giftProperty]);

  const cartUnits = (cart) =>
    cart.items.reduce((total, line) => (isStaleGiftLine(line) ? total : total + line.quantity), 0);

  /** Savings are integer dollars by design, so "$" + n is exact. */
  const money = (n) => `$${n}`;

  const savedFor = (units) => (units <= 0 ? 0 : TIERS[Math.min(units, CAP) - 1]);

  /**
   * The sentence the shopper reads, wherever they read it. Mirrors
   * snippets/jewelry-save-message.liquid exactly — same three templates, same
   * tokens — so a server render and a client update are never worded
   * differently.
   */
  const messageFor = (units) => {
    const saved = savedFor(units);

    // Clamped: there is no rung past the cap, and an unclamped figure would
    // be a saving checkout can never honour.
    const nextsave = units >= CAP ? MAXSAVE : TIERS[units];

    const values = {
      first: money(FIRST),
      step: money(STEP),
      saved: money(saved),
      nextsave: money(nextsave),
      nextstep: money(units >= CAP ? 0 : nextsave - saved),
      maxsave: money(MAXSAVE),
      count: units,
      cap: CAP,
    };

    let template = CONFIG.strings.start;
    if (units >= CAP) template = CONFIG.strings.complete;
    else if (units > 0) template = CONFIG.strings.progress;

    return { text: fill(template, values), template, values, saved };
  };

  const sectionInnerHTML = (html, selector) => {
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : null;
  };

  /* ------------------------------------------------------------ cart calls */

  const getCart = () => fetch(`${routes.cart_url}.js`).then((response) => response.json());

  const removeLine = (key) =>
    fetch(routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({ id: key, quantity: 0 }),
    }).then((response) => response.json());

  /* --------------------------------------------------------- section paint */

  /**
   * The same section contract Dawn's cart-items uses, so a cleanup-driven
   * change repaints the cart exactly the way a shopper-driven one does.
   */
  const sectionsToRender = () => {
    const sections = [];

    if (document.querySelector('cart-drawer-items')) {
      sections.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' });
    }

    const items = document.getElementById('main-cart-items');
    if (items) sections.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });

    const footer = document.getElementById('main-cart-footer');
    if (footer) sections.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });

    if (document.getElementById('cart-live-region-text')) {
      sections.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });
    }

    if (document.getElementById('cart-icon-bubble')) {
      sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }

    return sections;
  };

  const repaint = async () => {
    const targets = sectionsToRender();
    if (targets.length === 0) return getCart();

    const response = await fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        sections: targets.map((target) => target.section),
        sections_url: window.location.pathname,
      }),
    });

    const cart = await response.json();

    const drawer = document.querySelector('cart-drawer');
    const footer = document.getElementById('main-cart-footer');
    const empty = cart.item_count === 0;

    if (drawer) drawer.classList.toggle('is-empty', empty);
    if (footer) footer.classList.toggle('is-empty', empty);
    document.querySelectorAll('cart-items, cart-drawer-items').forEach((element) => {
      element.classList.toggle('is-empty', empty);
    });

    targets.forEach((target) => {
      const host = document.getElementById(target.id);
      if (!host || !cart.sections || !cart.sections[target.section]) return;

      const element = host.querySelector(target.selector) || host;
      const html = sectionInnerHTML(cart.sections[target.section], target.selector);
      if (html !== null) element.innerHTML = html;
    });

    return cart;
  };

  /* ----------------------------------------------------------- the element */

  class JwSaveLadder extends HTMLElement {
    connectedCallback() {
      this.slots = this.querySelector('[data-jw-save-slots]');
      this.message = this.querySelector('[data-jw-save-message]');
      this.status = this.querySelector('[data-jw-save-status]');

      ladders.add(this);

      // A rail that arrives with a repainted section was just rendered by
      // Liquid from the live cart — it is FRESHER than engine.cart, which
      // still holds the state from the last completed sync. Rendering from
      // the cache here briefly rewrote a correct paint with stale numbers
      // (drop 2 units to 1 in the drawer: fresh HTML said $10, the cache
      // said $15 until the debounced sync landed). So never paint from the
      // cache on connect; if the cache disagrees with what Liquid stamped,
      // schedule a sync and let the fetch settle who is right.
      if (engine.cart && cartUnits(engine.cart) !== Number(this.dataset.units || '0')) {
        engine.schedule();
      }
    }

    disconnectedCallback() {
      ladders.delete(this);
    }

    render(cart, announce) {
      const units = cartUnits(cart);
      const { text, template, values } = messageFor(units);
      const capped = Math.min(units, CAP);

      // Nodes, not text: the amounts keep the emphasis Liquid gave them on
      // the first render. `text` stays plain for aria-valuetext below.
      if (this.message) {
        this.message.textContent = '';
        this.message.appendChild(fillNodes(template, values));
      }

      if (this.slots) {
        this.slots.setAttribute('aria-valuenow', String(capped));
        this.slots.setAttribute('aria-valuetext', text);

        this.slots.querySelectorAll('.jw-ladder__slot').forEach((slot) => {
          // Liquid stamped the pieces this slot needs; the rule lives there, once.
          const need = Number(slot.dataset.need);
          let state = 'empty';

          if (units >= need) state = 'filled';
          else if (need === units + 1) state = 'next';

          if (slot.dataset.state === state) return;

          if (state === 'filled') {
            slot.classList.remove('is-won');
            void slot.offsetWidth; // restart the animation on a re-won rung
            slot.classList.add('is-won');
          }

          slot.dataset.state = state;
        });
      }

      // aria-valuetext on an unfocused progressbar is silent in every screen
      // reader; the live region is what actually announces the change. Only
      // written on cart-driven renders so page load stays quiet.
      if (announce && this.status && this.status.textContent !== text) {
        this.status.textContent = text;
      }

      this.dataset.units = String(units);
    }
  }

  /* ----------------------------------------------------------- the engine */

  const engine = {
    cart: null,
    busy: false,
    queued: false,
    timer: null,

    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.sync(), SYNC_DEBOUNCE);
    },

    async sync() {
      if (this.busy) {
        this.queued = true;
        return;
      }

      this.busy = true;

      try {
        let cart = await getCart();
        let cleaned = false;

        // Remove leftovers from the retired gift ladder — see file header.
        let guard = 0;
        let stale = cart.items.filter(isStaleGiftLine);
        while (stale.length > 0 && guard < CLEANUP_GUARD) {
          guard += 1;
          cart = await removeLine(stale[0].key);
          cleaned = true;
          stale = cart.items.filter(isStaleGiftLine);
        }

        if (cleaned) {
          try {
            cart = await repaint();
          } catch (error) {
            // Painting failed but the lines are already right. Hand off to the
            // theme's own cart components so the shopper still sees the truth.
            console.warn('[Aurum stack & save]', error);
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'jw-save-ladder', cartData: cart });
          }
        }

        this.cart = cart;
        ladders.forEach((ladder) => ladder.render(cart, true));

        // The homepage band's three explainer cards live OUTSIDE the custom
        // element (they are section markup, not rail markup), so the element
        // render above cannot reach them. Each card carries the unit count
        // that lights it in data-need, same contract as the rail slots.
        const units = cartUnits(cart);
        document.querySelectorAll('[data-jw-save-card]').forEach((card) => {
          card.dataset.state = units >= Number(card.dataset.need) ? 'filled' : 'empty';
        });
      } catch (error) {
        // The ladder is an enhancement. A failure here must never stop someone
        // from buying, so it is logged and swallowed.
        console.warn('[Aurum stack & save]', error);
      } finally {
        this.busy = false;

        if (this.queued) {
          this.queued = false;
          this.schedule();
        }
      }
    },
  };

  /* -------------------------------------------------------------- wiring */

  // Defined only now: connectedCallback reads `engine`, and an upgrade fires
  // synchronously inside define() for ladders already in the document.
  if (!customElements.get('jw-save-ladder')) {
    customElements.define('jw-save-ladder', JwSaveLadder);
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, () => engine.schedule());

  // The stale-gift cleanup must not wait for the shopper's first cart change,
  // and a cart restored from another device can hold leftovers the server
  // render did not see. Everything else starts correct from Liquid.
  if (CONFIG.staleGifts > 0) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => engine.sync(), { once: true });
    } else {
      engine.sync();
    }
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) engine.schedule();
  });
})();
